
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { MarketAnalysisUsageService, UsageCheckResult } from '@/services/MarketAnalysisUsageService';
import { toast } from 'sonner';

export const useMarketAnalysisUsage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if user can use market analysis
  const { data: usageCheck, isLoading, refetch } = useQuery({
    queryKey: ['marketAnalysisUsage', user?.id],
    queryFn: async (): Promise<UsageCheckResult> => {
      if (!user?.id) {
        return {
          allowed: false,
          reason: 'Not authenticated',
          remainingDaily: 0,
          remainingMonthly: 0,
          limits: { daily: 0, monthly: 0 },
          usage: { daily: 0, monthly: 0 },
          source: 'free'
        };
      }
      
      return await MarketAnalysisUsageService.canUseMarketAnalysis(user.id);
    },
    enabled: !!user?.id,
    refetchInterval: 60000, // Refetch every minute to keep usage current
  });

  // Mutation to increment usage - SIMPLIFIED
  const incrementUsageMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Not authenticated');
      console.log('🚀 [HOOK] Starting usage increment for user:', user.id);
      
      const success = await MarketAnalysisUsageService.incrementMarketAnalysisUsage(user.id);
      console.log('🚀 [HOOK] Usage increment result:', success);
      
      if (!success) {
        throw new Error('Failed to increment usage');
      }
      
      return success;
    },
    onSuccess: () => {
      console.log('✅ [HOOK] Usage incremented successfully');
      // Refetch usage data after successful increment
      queryClient.invalidateQueries({ queryKey: ['marketAnalysisUsage', user?.id] });
    },
    onError: (error) => {
      console.error('❌ [HOOK] Usage increment failed:', error);
    }
  });

  // SIMPLIFIED helper to check and increment usage
  const useMarketAnalysis = async (): Promise<boolean> => {
    console.log('🎯 [HOOK] useMarketAnalysis called');
    console.log('🎯 [HOOK] Current usage check:', usageCheck);
    
    // Check if user is allowed to use the service
    if (!usageCheck?.allowed) {
      console.log('❌ [HOOK] Usage not allowed');
      const reason = usageCheck?.source === 'affiliate' 
        ? 'Bạn đã hết quota hôm nay. Liên hệ affiliate để tăng giới hạn hoặc nâng cấp gói trả phí.'
        : 'Bạn đã hết quota miễn phí. Vui lòng nâng cấp gói trả phí để tiếp tục sử dụng.';
      
      toast.error(reason);
      return false;
    }

    try {
      console.log('📈 [HOOK] Attempting to increment usage...');
      await incrementUsageMutation.mutateAsync();
      console.log('✅ [HOOK] Usage increment completed successfully');
      return true;
    } catch (error) {
      console.error('❌ [HOOK] Usage increment failed:', error);
      toast.error('Failed to record usage', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      return false;
    }
  };

  return {
    // Usage check data
    canUse: usageCheck?.allowed || false,
    remainingDaily: usageCheck?.remainingDaily || 0,
    remainingMonthly: usageCheck?.remainingMonthly || 0,
    dailyLimit: usageCheck?.limits.daily || 0,
    monthlyLimit: usageCheck?.limits.monthly || 0,
    dailyUsage: usageCheck?.usage.daily || 0,
    monthlyUsage: usageCheck?.usage.monthly || 0,
    source: usageCheck?.source || 'free',
    reason: usageCheck?.reason,
    
    // Loading states
    isLoading,
    isIncrementing: incrementUsageMutation.isPending,
    
    // Actions
    useMarketAnalysis,
    refreshUsage: refetch,
    
    // Raw usage check data
    usageCheck,
    
    // Error information
    incrementError: incrementUsageMutation.error
  };
};
